import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';

interface RailLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The one workspace layout, two columns each with their own header: the dark
 * rail runs full height with the brand cap on top; the content column runs
 * the WorkspaceHeader (app pill › tabs › search) above the paper ground.
 * Every page that renders a Sidebar (mail, tasks, notes, calendar, triage,
 * surveys) goes through here so the frame can never drift between
 * workspaces again.
 */
export function RailLayout({ sidebar, children, className }: RailLayoutProps) {
  return (
    <div className={cn('relative flex flex-1 min-h-0 overflow-hidden', className)}>
      <div className="max-md:w-0 md:flex md:w-55 md:shrink-0 md:flex-col">
        <BrandCap />
        <div className="min-h-0 flex-1">{sidebar}</div>
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <WorkspaceHeader />
        <div className="flex-1 min-h-0 min-w-0 flex flex-col md:px-4 md:pb-4">{children}</div>
      </div>
    </div>
  );
}

/** The rail's head: app mark + wordmark, same height as the WorkspaceHeader
 * so the two column headers read as one band. */
function BrandCap() {
  return (
    <div className="hidden h-13 shrink-0 items-center gap-1.5 surface-dark px-7 md:flex">
      <img src="/sundae.svg" alt="" className="h-6 w-auto mb-[0.3em]" />
      <span className="text-xl font-extrabold tracking-tight text-white">
        sundae<span aria-hidden className="ml-[0.06em] -mb-[.06em] inline-block size-[0.15em] rounded-full align-baseline" style={{ background: 'var(--brand-cherry)' }} />
      </span>
    </div>
  );
}
